/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { VisualBrick, registerBrick, QueryResult, ListDef, GlobalProperties, BrickContext } from 'olympe';

import React from 'react';
import ReactDOM from 'react-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Observable, of } from 'rxjs';
import { combineLatestWith, switchMap } from 'rxjs/operators';

import { cssToSxProps, ifNotTransparent, ifNotNull, getLogger } from '@olympeio/core';

/**
 * Provide a "List Renderer" visual component using MUI
 */
export default class TileDisplayer extends VisualBrick {
    /**
     * @override
     * @param {!BrickContext} $
     * @returns {!Observable}
     */
    setupExecution($) {
        // List of properties to observe normally
        const properties = [
            'Item Renderer',
            'Data',
            'Border Color',
            'Border Radius',
            'Border Width',
            'CSS Property',
            'Default Color',
            'Hidden',
        ];

        const sizeObservable = $.observe('Data', false).pipe(
            switchMap((list) => {
                let size;
                if (Array.isArray(list)) {
                    size = of(list.length);
                } else if (list instanceof QueryResult) {
                    size = of(list.size());
                } else if (list instanceof ListDef) {
                    size = list.observeSize();
                } else {
                    getLogger('Tile Displayer').warn('Data property of List is not a List, Array or QueryResult.');
                    size = of(0);
                }
                return size;
            })
        );

        // Final observable
        return sizeObservable.pipe(combineLatestWith(properties.map((p) => $.observe(p))));
    }

    /**
     * @override
     * @param {!Element} parent
     * @param {!Element} element
     * @returns {function(): void}
     */
    updateParent(parent, element) {
        parent.style.overflow = 'visible';
        ReactDOM.render(element, parent);
        return () => ReactDOM.unmountComponentAtNode(parent);
    }

    /**
     * @override
     * @param {!BrickContext} $
     * @param {!Array} properties
     * @returns {Element}
     */
    render($, properties) {
        // List properties
        const [
            dataSize,
            renderer,
            elements,
            borderColor,
            borderRadius,
            borderWidth,
            cssProperty,
            defaultColor,
            hidden,
        ] = properties;

        let cachedItemsPerLine = 1;
        // Element
        return (
            !hidden && (
                <Box
                    sx={{
                        // Full width and height
                        width: 1,
                        height: 1,

                        // Scroll
                        overflow: 'auto',

                        // Border and background
                        ...ifNotTransparent('borderColor', borderColor),
                        ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                        ...ifNotNull('borderWidth', `${borderWidth}px`, borderWidth),
                        ...ifNotTransparent('borderStyle', 'solid', borderColor),
                        ...ifNotTransparent('backgroundColor', defaultColor),
                        ...ifNotNull('boxSizing', 'border-box', borderWidth),

                        // Additional
                        ...cssToSxProps(cssProperty),
                    }}
                >
                    {($.get('Data') && renderer) || !$.get(GlobalProperties.EDITION, true) ? (
                        // Only render if there is a list and a renderer, OR if we are not in draw
                        elements.map((item, rank) => {
                            return (
                                <Box
                                    key={rank}
                                    sx={{
                                        position: 'absolute',
                                        display: 'inline-block',
                                    }}
                                    // Item rendering
                                    ref={(el) => {
                                        if (el && renderer) {
                                            // Create renderer
                                            const renderer$ = $.runner(renderer)
                                                .set('Current Item', item)
                                                .set('Current Rank', rank)
                                                .set('Current List', $.get('Data'))
                                                .setParentElement(el);

                                            $.observe('item Width', false)
                                                .pipe(
                                                    combineLatestWith(
                                                        ['item Height', 'Width', 'margin'].map((p) => $.observe(p))
                                                    )
                                                )
                                                .subscribe(([itemWidth, itemHeight, width, margin]) => {
                                                    renderer$.set('Width', itemWidth);
                                                    renderer$.set('Height', itemHeight);

                                                    el.style.width = `${itemWidth}px`;
                                                    el.style.height = `${itemHeight}px`;
                                                    el.style.overflow = 'visible';

                                                    // Computing position : Width is the position into the row
                                                    const itemsPerLine = Math.max(
                                                        Math.floor(width / (itemWidth + margin)),
                                                        1
                                                    );
                                                    const line = Math.floor(rank / itemsPerLine);
                                                    const column = rank % itemsPerLine;

                                                    el.style.top = `${line * (itemHeight + margin)}px`;
                                                    el.style.left = `${column * (itemWidth + margin)}px`;

                                                    if (itemsPerLine !== cachedItemsPerLine) {
                                                        cachedItemsPerLine = itemsPerLine;
                                                        const requiredHeight =
                                                            Math.ceil(dataSize / itemsPerLine) * (itemHeight + margin);
                                                        $.set('Height', requiredHeight);
                                                    }
                                                });
                                        }
                                    }}
                                ></Box>
                            );
                        })
                    ) : (
                        // No list or renderer
                        <Box sx={{ backgroundColor: 'lightgrey', width: 1, height: 1, overflow: 'hidden' }}>
                            <Typography sx={{ color: 'black', padding: 1 }}>
                                <b>List</b>
                                <br />
                                The following properties have to be defined for the component to render:
                                <br />- <code>Data</code>
                                <br />- <code>Item Renderer</code>
                            </Typography>
                        </Box>
                    )}
                </Box>
            )
        );
    }
}

registerBrick('018ad691462867ebe0e0', TileDisplayer);
